import { useState } from 'react';
import { PRODUCTS } from '../store/productData';
import { useLanguage } from '../store/LanguageContext';
import { createQuote } from '../lib/api';
import SEOHead from '../components/SEOHead';
import { CheckCircle, Loader2 } from 'lucide-react';

export default function QuotePage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', vatNumber: '', type: 'particulier',
    productId: '', area: '', message: '', postcode: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      const selectedProduct = PRODUCTS.find(p => p.id === form.productId);
      const areaNum = parseFloat(form.area) || 0;
      const noteParts = [
        `Type: ${form.type}`,
        form.company ? `Bedrijf: ${form.company}` : '',
        form.vatNumber ? `BTW: ${form.vatNumber}` : '',
        form.postcode ? `Postcode: ${form.postcode}` : '',
        selectedProduct ? `Product: ${selectedProduct.name}` : '',
        areaNum > 0 ? `Oppervlakte: ${areaNum} m²` : '',
        form.message ? `Bericht: ${form.message}` : '',
      ].filter(Boolean).join(' | ');

      await createQuote({
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone || undefined,
        notes_nl: noteParts,
      });
      setSubmitted(true);
    } catch {
      setSubmitError('Aanvraag mislukt. Probeer het later opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 md:pt-24 min-h-screen flex items-center justify-center bg-[#faf7f3]">
        <SEOHead page="quote" />
        <div className="text-center max-w-md mx-auto p-10 bg-white rounded-lg shadow-lg">
          <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mb-4 mx-auto"><CheckCircle size={32} className="text-green-600" /></div>
          <h2 className="text-2xl font-bold mb-3">{t('quote.successTitle')}</h2>
          <p className="text-[#8a7e74] mb-6">{t('quote.successDesc')}</p>
          <button onClick={() => setSubmitted(false)} className="btn-primary">{t('quote.newRequest')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 md:pt-24">
      <SEOHead page="quote" />
      <section className="bg-[#1a1a1a] text-white section-padding !pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-[#c47a3a] text-sm font-semibold tracking-wider uppercase">{t('nav.quote')}</span>
          <h1 className="text-3xl md:text-5xl font-bold mt-2 mb-4">{t('quote.title')}</h1>
          <p className="text-gray-400 max-w-xl">{t('quote.desc')}</p>
        </div>
      </section>

      <section className="section-padding bg-[#faf7f3]">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold mb-4">{t('quote.iAm')}</h3>
              <div className="flex gap-3">
                {[['particulier', t('quote.private')], ['aannemer', t('quote.contractor')], ['architect', t('quote.architect')], ['groothandel', t('quote.wholesaler')]].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setForm(f => ({ ...f, type: val }))}
                    className={`flex-1 py-3 rounded-sm text-sm font-medium transition-all ${form.type === val ? 'bg-[#c47a3a] text-white' : 'bg-[#faf7f3] text-[#4a4a4a] hover:bg-gray-200'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold mb-4">{t('quote.contactInfo')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">{t('quote.name')} *</label>
                  <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">{t('quote.email')} *</label>
                  <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">{t('quote.phone')}</label>
                  <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">{t('quote.postcode')} *</label>
                  <input type="text" required value={form.postcode} onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))}
                    placeholder={t('quote.postcodeHint')} className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]" />
                </div>
                {(form.type === 'aannemer' || form.type === 'architect' || form.type === 'groothandel') && (
                  <>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-[#4a4a4a] mb-1">{t('quote.company')}</label>
                    <input type="text" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#4a4a4a] mb-1">BTW-nummer</label>
                    <input type="text" value={form.vatNumber || ''} onChange={e => setForm(f => ({ ...f, vatNumber: e.target.value }))}
                      placeholder="BE0123.456.789" className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]" />
                  </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold mb-4">{t('quote.projectDetails')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">{t('quote.product')}</label>
                  <select value={form.productId} onChange={e => setForm(f => ({ ...f, productId: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]">
                    <option value="">{t('quote.selectProduct')}</option>
                    {PRODUCTS.map(p => <option key={p.id} value={p.id}>{p.name} — €{p.pricePerM2}/m²</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">{t('quote.estimatedArea')}</label>
                  <input type="number" value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
                    placeholder={t('quote.areaHint')} className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]" />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-[#4a4a4a] mb-1">{t('quote.additionalInfo')}</label>
                <textarea rows={4} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder={t('quote.additionalInfoHint')} className="w-full px-4 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a] resize-none" />
              </div>
            </div>

            <button type="submit" disabled={submitting} className="btn-primary w-full !py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50">
              {submitting && <Loader2 size={18} className="animate-spin" />}
              {submitting ? 'Verzenden…' : t('quote.submit')}
            </button>
            {submitError && <p className="text-red-600 text-sm text-center bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</p>}
            <p className="text-xs text-center text-[#8a7e74]">{t('quote.submitNote')}</p>
          </form>
        </div>
      </section>
    </div>
  );
}
